import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { toast } from 'react-toastify';
import api from '@services/api';

interface AcademyAttendancePageProps {
  academyId: string;
}

interface PlayerAttendance {
  id: string;
  playerIdCode: string;
  name: string;
  sport: string;
  attendance: Record<string, { present: boolean | null; recordId: string | null }>;
}

interface WeekData {
  weekStart: string;
  weekEnd: string;
  weekDates: string[];
  players: PlayerAttendance[];
}

const AcademyAttendancePage: React.FC<AcademyAttendancePageProps> = ({ academyId }) => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weekData, setWeekData] = useState<WeekData | null>(null);
  const [attendanceState, setAttendanceState] = useState<Record<string, Record<string, boolean | null>>>({});
  
  // Filters
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'id'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  useEffect(() => {
    loadAttendance();
  }, [academyId, currentWeekStart, selectedSport, sortBy, sortOrder]);

  const getWeekStartDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const loadAttendance = async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate = getWeekStartDate(currentWeekStart);
      const params = new URLSearchParams({
        startDate,
        sport: selectedSport,
        sortBy,
        sortOrder,
      });
      
      const response = await api.get(`/academies/${academyId}/attendance/week?${params.toString()}`);
      const result = response.data;
      
      if (result.success) {
        setWeekData(result.data);
        // Initialize attendance state
        const state: Record<string, Record<string, boolean | null>> = {};
        result.data.players.forEach((player: PlayerAttendance) => {
          state[player.id] = {};
          result.data.weekDates.forEach((date: string) => {
            state[player.id][date] = player.attendance[date]?.present ?? null;
          });
        });
        setAttendanceState(state);
      } else {
        setError(result.message || 'Failed to load attendance');
      }
    } catch (err: any) {
      console.error('Error loading attendance:', err);
      setError('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (playerId: string, date: string, value: boolean) => {
    setAttendanceState(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [date]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!weekData) return;
    
    setSaving(true);
    try {
      // Save by date groups for efficiency
      const dateGroups: Record<string, Array<{ playerId: string; isPresent: boolean }>> = {};
      
      weekData.weekDates.forEach(date => {
        dateGroups[date] = [];
        Object.keys(attendanceState).forEach(playerId => {
          const value = attendanceState[playerId]?.[date];
          if (value !== null && value !== undefined) {
            dateGroups[date].push({
              playerId,
              isPresent: value
            });
          }
        });
      });
      
      // Save each day
      let savedCount = 0;
      for (const [date, records] of Object.entries(dateGroups)) {
        if (records.length > 0) {
          const result = await api.post(`/academies/${academyId}/attendance`, {
            date,
            attendance: records,
            sport: selectedSport !== 'all' ? selectedSport : null
          });
          if (!result.data.success) {
            throw new Error(result.data.message || 'Failed to save attendance');
          }
          savedCount += records.length;
        }
      }
      
      toast.success(`Attendance saved successfully (${savedCount} records)`);
      loadAttendance(); // Reload to get updated data
    } catch (err: any) {
      console.error('Error saving attendance:', err);
      toast.error(err.message || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkMark = async (date: string, markPresent: boolean) => {
    if (!weekData) return;
    
    try {
      const playerIds = weekData.players.map(p => p.id);
      
      const result = await api.post(`/academies/${academyId}/attendance/bulk`, {
        date,
        playerIds: markPresent ? playerIds : [],
        markAllPresent: markPresent,
        sport: selectedSport !== 'all' ? selectedSport : null
      });
      if (result.data.success) {
        toast.success(`Marked all ${markPresent ? 'present' : 'absent'} for ${new Date(date).toLocaleDateString()}`);
        loadAttendance();
      } else {
        throw new Error(result.data.message || 'Failed to mark attendance');
      }
    } catch (err: any) {
      console.error('Error bulk marking attendance:', err);
      toast.error(err.message || 'Failed to mark attendance');
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  const getDayName = (dateStr: string): string => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const sortedPlayers = weekData?.players ? [...weekData.players].sort((a, b) => {
    if (sortBy === 'name') {
      const comparison = a.name.localeCompare(b.name);
      return sortOrder === 'asc' ? comparison : -comparison;
    } else {
      const comparison = a.playerIdCode.localeCompare(b.playerIdCode);
      return sortOrder === 'asc' ? comparison : -comparison;
    }
  }) : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Attendance Tracking
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
        Track daily attendance for all players in your academy
      </Typography>

      {/* Week Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <IconButton onClick={() => navigateWeek('prev')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 200 }}>
              Week: {weekData ? `${formatDate(weekData.weekStart)} - ${formatDate(weekData.weekEnd)}` : 'Loading...'}
            </Typography>
            <IconButton onClick={() => navigateWeek('next')} size="small">
              <ArrowForwardIcon />
            </IconButton>
            <TextField
              type="date"
              label="Select Week Start (Monday)"
              size="small"
              value={getWeekStartDate(currentWeekStart)}
              onChange={(e) => {
                if (e.target.value) {
                  const selectedDate = new Date(e.target.value);
                  const day = selectedDate.getDay();
                  const diff = selectedDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
                  const monday = new Date(selectedDate.setDate(diff));
                  monday.setHours(0, 0, 0, 0);
                  setCurrentWeekStart(monday);
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Button variant="outlined" size="small" onClick={goToCurrentWeek}>
              Current Week
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Attendance'}
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sport</InputLabel>
            <Select value={selectedSport} onChange={(e) => setSelectedSport(e.target.value)} label="Sport">
              <MenuItem value="all">All Sports</MenuItem>
              <MenuItem value="Badminton">Badminton</MenuItem>
              <MenuItem value="Cricket">Cricket</MenuItem>
              <MenuItem value="Tennis">Tennis</MenuItem>
              <MenuItem value="Football">Football</MenuItem>
              <MenuItem value="Athletics">Athletics</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'name' | 'id')} label="Sort By">
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="id">Player ID</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : weekData && sortedPlayers.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Player</TableCell>
                {weekData.weekDates.map((date) => (
                  <TableCell key={date} align="center" sx={{ fontWeight: 700, minWidth: 120 }}>
                    <Box>
                      <Typography variant="caption" display="block">
                        {getDayName(date)}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(date)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 30, p: 0.5 }}
                          onClick={() => handleBulkMark(date, true)}
                          title="Mark all present"
                        >
                          <CheckCircleIcon fontSize="small" color="success" />
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 30, p: 0.5 }}
                          onClick={() => handleBulkMark(date, false)}
                          title="Mark all absent"
                        >
                          <CancelIcon fontSize="small" color="error" />
                        </Button>
                      </Box>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPlayers.map((player) => (
                <TableRow key={player.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {player.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {player.playerIdCode}
                      </Typography>
                      {player.sport && (
                        <Chip label={player.sport} size="small" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  {weekData.weekDates.map((date) => {
                    const isPresent = attendanceState[player.id]?.[date];
                    return (
                      <TableCell key={date} align="center">
                        <Checkbox
                          checked={isPresent === true}
                          indeterminate={isPresent === null}
                          onChange={(e) => {
                            const currentValue = attendanceState[player.id]?.[date];
                            let newValue: boolean | null;
                            if (currentValue === true) {
                              newValue = false;
                            } else if (currentValue === false) {
                              newValue = null;
                            } else {
                              newValue = true;
                            }
                            handleAttendanceChange(player.id, date, newValue);
                          }}
                          sx={{
                            color: isPresent === false ? 'error.main' : 'success.main',
                            '&.Mui-checked': {
                              color: 'success.main',
                            },
                            '&.MuiCheckbox-indeterminate': {
                              color: 'text.secondary',
                            },
                          }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No players found. {selectedSport !== 'all' && 'Try selecting a different sport.'}
        </Alert>
      )}
    </Box>
  );
};

export default AcademyAttendancePage;

